import { Heading } from "@/components/ui/heading";
import { CloseIcon, Icon, InfoIcon } from "@/components/ui/icon";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { VStack } from "@/components/ui/vstack";
import { Text } from "@/components/ui/text";
import { WalletDTO } from "@/generated/api/models";
import { useSumFormatter } from "@/utils/useSumFormatter";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Input, InputField, InputSlot } from "@/components/ui/input";
import { parseCurrencyInput } from "@/utils/currency.parser";
import { getI18n } from "react-i18next";
import { centsToValue } from "@/utils/currency.precision";
import { Keyboard } from "react-native";
import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";
import { WalletPayoutAlerts } from "./wallet.payout.alerts";
import { Button, ButtonText } from "@/components/ui/button";
import {
  getUserPayoutControllerGetPayoutQueryKey,
  getUserWalletControllerGetWalletQueryKey,
  useUserControllerMe,
  useUserPayoutControllerCancelPayout,
  useUserPayoutControllerConfirmPayout,
  useUserPayoutControllerCreatePayout,
  useUserPayoutControllerGetPayout,
} from "@/generated/api/api";
import { getName } from "@/utils/name";
import { generateUid } from "@/utils/uid.generator";
import { useQueryClient } from "@tanstack/react-query";
import { is40x } from "@/utils/error.checker";
import { useToast } from "@/utils/useToast";
import { Spinner } from "@/components/ui/spinner";
import { SkeletonText } from "@/components/ui/skeleton";

export function WalletPayoutModalConfirmation({
  wallet,
  amount,
  onClose,
  onBack,
}: {
  wallet: WalletDTO;
  amount: number;
  onClose: () => void;
  onBack: () => void;
}) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { data: user } = useUserControllerMe();

  const recipientName = user?.beneficiary && getName(user.beneficiary);
  const recipientIBAN = user?.beneficiary && user.beneficiary.iban;

  const [idempotencyKey] = useState(generateUid());

  const {
    mutate,
    error: createError,
    data: createResponse,
  } = useUserPayoutControllerCreatePayout();

  const { data, error: getError } = useUserPayoutControllerGetPayout(
    idempotencyKey,
    {
      query: {
        enabled: !!createResponse,
        retry: (failureCount, error) => {
          if (is40x(error)) {
            return false;
          }

          return failureCount < 3;
        },
        refetchInterval: (query) => {
          const data = query.state.data;
          if (data?.status === "EXECUTING" || data?.status === "PENDING") {
            return 3000;
          }

          return false;
        },
      },
    }
  );

  const {
    mutate: mutateConfirmPayout,
    isPending: confirmIsLoading,
    isSuccess: confirmIsSuccess,
    data: confirmResponse,
  } = useUserPayoutControllerConfirmPayout();

  const { mutate: mutateCancelPayout } = useUserPayoutControllerCancelPayout();

  useEffect(() => {
    mutate({
      data: {
        amount,
        currency: wallet.currency,
        reference: idempotencyKey,
      },
    });
  }, []);

  const [seconds, setSeconds] = useState<number | undefined>();

  useEffect(() => {
    if (createResponse || confirmResponse) {
      queryClient.invalidateQueries({
        queryKey: getUserPayoutControllerGetPayoutQueryKey(idempotencyKey),
        refetchType: "all",
      });
    }
  }, [createResponse, confirmResponse, queryClient, idempotencyKey]);

  useEffect(() => {
    if (!data?.validUntil) {
      return;
    }

    const validUntil = new Date(data.validUntil as string);

    const interval = setInterval(() => {
      const secondsLeft = Math.floor(
        (validUntil.getTime() - Date.now()) / 1000
      );

      const safeSecondsLeft = Math.max(secondsLeft, 0);
      setSeconds(safeSecondsLeft);

      if (secondsLeft < 1 && !confirmIsLoading && !confirmIsSuccess) {
        showToast(
          "warning",
          "Time is out for payout request. Please try again."
        );

        clearInterval(interval);
        handleBack();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [data]);

  useEffect(() => {
    if (createError || getError) {
      return handleBack();
    }
  }, [createError, getError]);

  useEffect(() => {
    if (data && data.status === "EXECUTED") {
      queryClient.invalidateQueries({
        refetchType: "all",
        predicate: ({ queryKey }) => {
          return (
            queryKey.includes(getUserWalletControllerGetWalletQueryKey()[0]) ||
            queryKey.includes("transactions") ||
            !!queryKey.find((s) =>
              typeof s === "string" ? s.includes("transactions") : false
            )
          );
        },
      });
      showToast("success", "Payout succeeded");
      onClose();
    }
  }, [data]);

  const handleConfirm = () => {
    if (data) {
      mutateConfirmPayout({
        payoutId: data.reference,
      });
    }
  };

  const handleClose = () => {
    if (data?.status) {
      mutateCancelPayout({
        payoutId: idempotencyKey,
      });
    }
    onClose();
  };

  const handleBack = () => {
    if (data?.status) {
      mutateCancelPayout({
        payoutId: idempotencyKey,
      });
    }
    onBack();
  };

  const isZeroAmountOrLess =
    data &&
    data.amount !== undefined &&
    data.fee !== undefined &&
    data.amount - data.fee <= 0;

  const { formatCents } = useSumFormatter({
    asset: wallet.currency,
  });

  const payoutButtonDisabled =
    data?.status !== "WAITING" || confirmIsLoading || isZeroAmountOrLess;

  return (
    <>
      <ModalHeader>
        <Heading>Review & Confirm</Heading>
        <ModalCloseButton className="self-start mt-2" onPress={handleClose}>
          <Icon
            as={CloseIcon}
            size="md"
            className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
          />
        </ModalCloseButton>
      </ModalHeader>
      <ModalBody>
        <VStack className="gap-2">
          {isZeroAmountOrLess && <WalletPayoutAlerts.ZeroAmount />}
          {data?.status !== "WAITING" && <LoadingState />}
          {!isZeroAmountOrLess && data?.status === "WAITING" && (
            <VStack className="gap-1">
              {data.amount !== undefined &&
                data.requestedAmount !== data.amount && (
                  <WalletPayoutAlerts.AmountChanged />
                )}
              <Text>
                Recipient name: <Text bold>{recipientName}</Text>
              </Text>
              <Text>
                Recipient IBAN: <Text bold>{recipientIBAN}</Text>
              </Text>
              <Text>
                Payout amount: <Text bold>{formatCents(data.amount ?? 0)}</Text>
              </Text>
              <Text>
                Recipient gets:{" "}
                <Text bold>
                  {formatCents((data.amount ?? 0) - (data.fee ?? 0))}
                </Text>
              </Text>
              <Text>
                Fee: <Text bold>{formatCents(data.fee ?? 0)}</Text>
              </Text>
              {data.fee !== undefined && data.fee > 0 ? (
                <WalletPayoutAlerts.NonZeroFee
                  formatCents={formatCents}
                  fee={data.fee}
                  minNoFeeAmount={5000}
                />
              ) : (
                <WalletPayoutAlerts.ZeroFee />
              )}
            </VStack>
          )}
        </VStack>
      </ModalBody>

      <ModalFooter className="flex-row items-end">
        <Button onPress={handleBack} variant="outline">
          <ButtonText>Back</ButtonText>
        </Button>
        <Button isDisabled={payoutButtonDisabled} onPress={handleConfirm}>
          <ButtonText>
            {seconds !== undefined && !payoutButtonDisabled && seconds <= 60
              ? `Make payout (${Math.max(seconds, 0)})`
              : `Make payout`}
          </ButtonText>
        </Button>
      </ModalFooter>
    </>
  );
}

function LoadingState() {
  return (
    <VStack className="gap-2 justify-center">
      <Spinner size="large" />
      <SkeletonText _lines={4} className="h-3" />
    </VStack>
  );
}
